import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2, Upload, Link, Users, ArrowRight, ArrowLeft, Sparkles,
  Building2, AlertTriangle, CheckCircle, MessageSquare, Lock,
  TrendingUp, Target, Briefcase, FileText, Plus, X
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Step 1: Input investors
function InvestorInputStep({ onNext, investorData, setInvestorData }) {
  const [inputMethod, setInputMethod] = useState("url");
  const [url, setUrl] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [manualInvestors, setManualInvestors] = useState([{ name: "", firm: "" }]);
  const [parsing, setParsing] = useState(false);
  // Extracted investors for review (from URL/CSV)
  const [extractedInvestors, setExtractedInvestors] = useState([]);
  const [extractionSource, setExtractionSource] = useState(null); // "url" or "csv"

  const handleUrlParse = async () => {
    if (!url.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    setParsing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/intel/parse-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.investors && data.investors.length > 0) {
          // Show in review table instead of immediately going to next step
          setExtractedInvestors(data.investors);
          setExtractionSource("url");
          toast.success(`Found ${data.investors.length} investors - review below`);
        } else {
          toast.error("No investors found at this URL. Try manual entry.");
        }
      } else {
        toast.error("Failed to parse URL. Try manual entry.");
      }
    } catch (error) {
      toast.error("Failed to parse URL. Try manual entry.");
    } finally {
      setParsing(false);
    }
  };

  const handleCsvUpload = async (file) => {
    setCsvFile(file);
    setParsing(true);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/intel/parse-csv`, {
        method: "POST",
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.investors && data.investors.length > 0) {
          // Show in review table instead of immediately going to next step
          setExtractedInvestors(data.investors);
          setExtractionSource("csv");
          toast.success(`Found ${data.investors.length} investors - review below`);
        } else {
          toast.error("No investors found in CSV");
        }
      } else {
        toast.error("Failed to parse CSV");
      }
    } catch (error) {
      toast.error("Failed to parse CSV");
    } finally {
      setParsing(false);
    }
  };

  const handleManualSubmit = () => {
    const validInvestors = manualInvestors.filter(inv => inv.name.trim() && inv.firm.trim());
    if (validInvestors.length === 0) {
      toast.error("Please add at least one investor");
      return;
    }
    setInvestorData({ source: "manual", investors: validInvestors });
    onNext();
  };

  // Handle proceeding with extracted investors after review
  const handleProceedWithExtracted = () => {
    const validInvestors = extractedInvestors.filter(inv => inv.name.trim() && inv.firm.trim());
    if (validInvestors.length === 0) {
      toast.error("Please keep at least one investor");
      return;
    }
    setInvestorData({ 
      source: extractionSource, 
      investors: validInvestors,
      ...(extractionSource === "url" ? { sourceUrl: url } : { fileName: csvFile?.name })
    });
    onNext();
  };

  // Update an extracted investor row
  const updateExtractedRow = (index, field, value) => {
    const updated = [...extractedInvestors];
    updated[index][field] = value;
    setExtractedInvestors(updated);
  };

  // Remove an extracted investor row
  const removeExtractedRow = (index) => {
    setExtractedInvestors(extractedInvestors.filter((_, i) => i !== index));
  };

  // Add a new row to extracted list
  const addExtractedRow = () => {
    setExtractedInvestors([...extractedInvestors, { name: "", firm: "" }]);
  };

  // Clear extraction and start over
  const clearExtraction = () => {
    setExtractedInvestors([]);
    setExtractionSource(null);
  };

  const addManualRow = () => {
    setManualInvestors([...manualInvestors, { name: "", firm: "" }]);
  };

  const removeManualRow = (index) => {
    setManualInvestors(manualInvestors.filter((_, i) => i !== index));
  };

  const updateManualRow = (index, field, value) => {
    const updated = [...manualInvestors];
    updated[index][field] = value;
    setManualInvestors(updated);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Add Your Investor List</h2>
        <p className="text-slate-600">Paste a URL, upload a CSV, or enter investors manually</p>
      </div>

      <Tabs value={inputMethod} onValueChange={setInputMethod} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="url" className="gap-2">
            <Link className="w-4 h-4" />
            URL
          </TabsTrigger>
          <TabsTrigger value="csv" className="gap-2">
            <Upload className="w-4 h-4" />
            CSV
          </TabsTrigger>
          <TabsTrigger value="manual" className="gap-2">
            <Users className="w-4 h-4" />
            Manual
          </TabsTrigger>
        </TabsList>

        <TabsContent value="url" className="mt-6">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label>Event or Investor List URL</Label>
                <p className="text-sm text-slate-500 mb-2">
                  Paste a link to an event page, LinkedIn list, or any page with investor names
                </p>
                <Input
                  placeholder="https://bio-europe.com/attendees..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  disabled={extractedInvestors.length > 0 && extractionSource === "url"}
                />
              </div>
              {extractedInvestors.length === 0 || extractionSource !== "url" ? (
                <Button 
                  onClick={handleUrlParse} 
                  disabled={parsing || !url.trim()}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                >
                  {parsing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Parsing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Extract Investors
                    </>
                  )}
                </Button>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="csv" className="mt-6">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label>Upload CSV File</Label>
                <p className="text-sm text-slate-500 mb-2">
                  CSV should have columns for investor name and firm name
                </p>
                {extractedInvestors.length === 0 || extractionSource !== "csv" ? (
                  <div 
                    className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-teal-500 transition-colors cursor-pointer"
                    onClick={() => document.getElementById('csv-upload').click()}
                  >
                    <Upload className="w-10 h-10 mx-auto mb-3 text-slate-400" />
                    <p className="text-slate-600">
                      {csvFile ? csvFile.name : "Click to upload or drag & drop"}
                    </p>
                    <p className="text-sm text-slate-400 mt-1">CSV files only</p>
                    <input
                      id="csv-upload"
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={(e) => e.target.files[0] && handleCsvUpload(e.target.files[0])}
                    />
                  </div>
                ) : (
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 text-center">
                    <CheckCircle className="w-6 h-6 mx-auto mb-2 text-teal-600" />
                    <p className="text-teal-700 font-medium">{csvFile?.name}</p>
                    <p className="text-sm text-teal-600">File uploaded successfully</p>
                  </div>
                )}
              </div>
              {parsing && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                  <span className="ml-2 text-slate-600">Processing CSV...</span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="manual" className="mt-6">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label>Enter Investors</Label>
                <p className="text-sm text-slate-500 mb-4">
                  Add investor names and their VC firm
                </p>
                
                <div className="space-y-3">
                  {manualInvestors.map((inv, idx) => (
                    <div key={idx} className="flex gap-3 items-center">
                      <Input
                        placeholder="Investor name"
                        value={inv.name}
                        onChange={(e) => updateManualRow(idx, "name", e.target.value)}
                        className="flex-1"
                      />
                      <Input
                        placeholder="VC Firm"
                        value={inv.firm}
                        onChange={(e) => updateManualRow(idx, "firm", e.target.value)}
                        className="flex-1"
                      />
                      {manualInvestors.length > 1 && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => removeManualRow(idx)}
                        >
                          <X className="w-4 h-4 text-slate-400" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                
                <Button variant="outline" onClick={addManualRow} className="mt-3 gap-2">
                  <Plus className="w-4 h-4" />
                  Add Another
                </Button>
              </div>
              
              <Button 
                onClick={handleManualSubmit}
                className="w-full bg-teal-600 hover:bg-teal-700"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Review Extracted Investors Table */}
      {extractedInvestors.length > 0 && (inputMethod === "url" || inputMethod === "csv") && (
        <Card className="border-teal-200 bg-teal-50/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-teal-600" />
                <CardTitle className="text-lg text-slate-900">
                  Review Extracted Investors ({extractedInvestors.length})
                </CardTitle>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearExtraction}
                className="text-slate-500 hover:text-slate-700"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            </div>
            <p className="text-sm text-slate-600">Edit or remove investors before proceeding</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Table Header */}
            <div className="flex gap-3 items-center px-1 text-sm font-medium text-slate-500">
              <div className="flex-1">Investor Name</div>
              <div className="flex-1">VC Firm</div>
              <div className="w-10"></div>
            </div>
            
            {/* Investor Rows */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {extractedInvestors.map((inv, idx) => (
                <div key={idx} className="flex gap-3 items-center">
                  <Input
                    placeholder="Investor name"
                    value={inv.name || ""}
                    onChange={(e) => updateExtractedRow(idx, "name", e.target.value)}
                    className="flex-1 bg-white"
                  />
                  <Input
                    placeholder="VC Firm"
                    value={inv.firm || ""}
                    onChange={(e) => updateExtractedRow(idx, "firm", e.target.value)}
                    className="flex-1 bg-white"
                  />
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => removeExtractedRow(idx)}
                    className="text-slate-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Add Row Button */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={addExtractedRow} 
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Investor
            </Button>

            {/* Proceed Button */}
            <Button 
              onClick={handleProceedWithExtracted}
              className="w-full bg-teal-600 hover:bg-teal-700 mt-4"
              disabled={extractedInvestors.filter(inv => inv.name?.trim() && inv.firm?.trim()).length === 0}
            >
              Continue with {extractedInvestors.filter(inv => inv.name?.trim() && inv.firm?.trim()).length} Investors
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Step 2: Startup profile input
function StartupProfileStep({ onNext, onBack, startupProfile, setStartupProfile }) {
  const [uploadingDeck, setUploadingDeck] = useState(false);
  const [useManual, setUseManual] = useState(false);

  const handleDeckUpload = async (file) => {
    setUploadingDeck(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/intel/parse-deck`, {
        method: "POST",
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setStartupProfile({
          ...startupProfile,
          ...data.profile,
          source: "deck"
        });
        toast.success("Profile extracted from deck!");
        setUseManual(true); // Show form to review/edit
      } else {
        toast.error("Failed to parse deck. Please fill manually.");
        setUseManual(true);
      }
    } catch (error) {
      toast.error("Failed to parse deck. Please fill manually.");
      setUseManual(true);
    } finally {
      setUploadingDeck(false);
    }
  };

  const handleSubmit = () => {
    if (!startupProfile.company_name?.trim()) {
      toast.error("Please enter your company name");
      return;
    }
    onNext();
  };

  const updateProfile = (field, value) => {
    setStartupProfile({ ...startupProfile, [field]: value });
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={onBack} className="gap-2 text-slate-600">
        <ArrowLeft className="w-4 h-4" />
        Back
      </Button>

      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Tell Us About Your Startup</h2>
        <p className="text-slate-600">We'll match investors to your specific focus</p>
      </div>

      {!useManual ? (
        <Card>
          <CardContent className="pt-6 space-y-6">
            <div 
              className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-teal-500 transition-colors cursor-pointer"
              onClick={() => document.getElementById('deck-upload').click()}
            >
              {uploadingDeck ? (
                <>
                  <Loader2 className="w-10 h-10 mx-auto mb-3 text-teal-600 animate-spin" />
                  <p className="text-slate-600">Analyzing your deck...</p>
                </>
              ) : (
                <>
                  <FileText className="w-10 h-10 mx-auto mb-3 text-slate-400" />
                  <p className="text-slate-900 font-medium">Upload Your Pitch Deck</p>
                  <p className="text-sm text-slate-500 mt-1">PDF or PPTX • We'll auto-fill your profile</p>
                </>
              )}
              <input
                id="deck-upload"
                type="file"
                accept=".pdf,.pptx,.ppt"
                className="hidden"
                onChange={(e) => e.target.files[0] && handleDeckUpload(e.target.files[0])}
              />
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">or fill manually</span>
              </div>
            </div>

            <Button 
              variant="outline" 
              onClick={() => setUseManual(true)}
              className="w-full"
            >
              Enter Details Manually
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Company Name *</Label>
                <Input
                  placeholder="Your startup name"
                  value={startupProfile.company_name || ""}
                  onChange={(e) => updateProfile("company_name", e.target.value)}
                />
              </div>
              <div>
                <Label>One-liner</Label>
                <Input
                  placeholder="What you do in one sentence"
                  value={startupProfile.one_liner || ""}
                  onChange={(e) => updateProfile("one_liner", e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Indication Focus</Label>
                <Select
                  value={startupProfile.indication || ""}
                  onValueChange={(v) => updateProfile("indication", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select indication" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oncology">Oncology</SelectItem>
                    <SelectItem value="neurology">Neurology</SelectItem>
                    <SelectItem value="immunology">Immunology</SelectItem>
                    <SelectItem value="rare_disease">Rare Disease</SelectItem>
                    <SelectItem value="infectious_disease">Infectious Disease</SelectItem>
                    <SelectItem value="cardiovascular">Cardiovascular</SelectItem>
                    <SelectItem value="metabolic">Metabolic</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Modality</Label>
                <Select
                  value={startupProfile.modality || ""}
                  onValueChange={(v) => updateProfile("modality", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select modality" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small_molecule">Small Molecule</SelectItem>
                    <SelectItem value="antibody">Antibody</SelectItem>
                    <SelectItem value="cell_therapy">Cell Therapy</SelectItem>
                    <SelectItem value="gene_therapy">Gene Therapy</SelectItem>
                    <SelectItem value="rna">RNA Therapeutics</SelectItem>
                    <SelectItem value="protein">Protein/Peptide</SelectItem>
                    <SelectItem value="diagnostic">Diagnostic</SelectItem>
                    <SelectItem value="platform">Platform/Tool</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Development Stage</Label>
                <Select
                  value={startupProfile.development_stage || ""}
                  onValueChange={(v) => updateProfile("development_stage", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discovery">Discovery</SelectItem>
                    <SelectItem value="preclinical">Preclinical</SelectItem>
                    <SelectItem value="phase_1">Phase 1</SelectItem>
                    <SelectItem value="phase_2">Phase 2</SelectItem>
                    <SelectItem value="phase_3">Phase 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Funding Stage</Label>
                <Select
                  value={startupProfile.funding_stage || ""}
                  onValueChange={(v) => updateProfile("funding_stage", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select funding stage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pre-seed">Pre-seed</SelectItem>
                    <SelectItem value="seed">Seed</SelectItem>
                    <SelectItem value="series_a">Series A</SelectItem>
                    <SelectItem value="series_b">Series B</SelectItem>
                    <SelectItem value="series_c">Series C+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button 
              onClick={handleSubmit}
              className="w-full bg-teal-600 hover:bg-teal-700"
            >
              Analyze Investors
              <Sparkles className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Gate/Score indicator component
function GateIndicator({ gate }) {
  if (!gate) return <span className="text-slate-300">—</span>;
  
  const { passed, confidence } = gate;
  const isLowConfidence = confidence === "low";
  
  if (passed) {
    return (
      <div className={`w-6 h-6 rounded flex items-center justify-center ${isLowConfidence ? 'bg-teal-100' : 'bg-teal-500'}`}>
        <CheckCircle className={`w-4 h-4 ${isLowConfidence ? 'text-teal-400' : 'text-white'}`} />
      </div>
    );
  }
  return (
    <div className={`w-6 h-6 rounded flex items-center justify-center ${isLowConfidence ? 'bg-red-100' : 'bg-red-500'}`}>
      <X className={`w-4 h-4 ${isLowConfidence ? 'text-red-400' : 'text-white'}`} />
    </div>
  );
}

// Score cell with value and detail
function ScoreCell({ score }) {
  if (!score) return <span className="text-slate-300">—</span>;
  
  const { value, detail, confidence } = score;
  const isLowConfidence = confidence === "low";
  
  let bgColor = "bg-slate-100";
  let textColor = "text-slate-600";
  
  if (value >= 70) {
    bgColor = isLowConfidence ? "bg-teal-50" : "bg-teal-100";
    textColor = isLowConfidence ? "text-teal-500" : "text-teal-700";
  } else if (value >= 50) {
    bgColor = isLowConfidence ? "bg-amber-50" : "bg-amber-100";
    textColor = isLowConfidence ? "text-amber-500" : "text-amber-700";
  } else {
    bgColor = isLowConfidence ? "bg-red-50" : "bg-red-100";
    textColor = isLowConfidence ? "text-red-400" : "text-red-600";
  }
  
  return (
    <div className={`px-2 py-1 rounded ${bgColor} ${textColor} text-xs`}>
      <div className="font-semibold">{value}/100</div>
      {detail && <div className="text-[10px] opacity-75 truncate max-w-[100px]" title={detail}>{detail}</div>}
    </div>
  );
}

// Step 3: Results - Rich Analysis Table
function ResultsStep({ investorData, startupProfile, onBack, onSignUp }) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const navigate = useNavigate();

  const analyzeInvestors = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/intel/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          investors: investorData.investors,
          startup_profile: startupProfile
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setResults(data);
      } else {
        toast.error("Analysis failed. Please try again.");
      }
    } catch (error) {
      toast.error("Analysis failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    analyzeInvestors();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="text-center py-16">
        <Loader2 className="w-12 h-12 mx-auto mb-4 text-teal-600 animate-spin" />
        <h3 className="text-xl font-semibold text-slate-900 mb-2">Analyzing Investors...</h3>
        <p className="text-slate-600">Matching against your startup profile using our algorithm</p>
      </div>
    );
  }

  const allResults = results?.analyzed || [];
  const freeResults = allResults.slice(0, 5);
  const lockedCount = allResults.length - 5;

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={onBack} className="gap-2 text-slate-600">
        <ArrowLeft className="w-4 h-4" />
        Back
      </Button>

      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Investor Analysis</h2>
        <p className="text-slate-600">
          {results?.total_analyzed || 0} investors analyzed for {startupProfile.company_name || "your startup"}
        </p>
        <div className="flex justify-center gap-4 mt-2 text-sm">
          <span className="text-teal-600">
            <Building2 className="w-4 h-4 inline mr-1" />
            {results?.platform_matches || 0} in Operon database
          </span>
          <span className="text-amber-600">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            {results?.inferred_matches || 0} inferred (lower confidence)
          </span>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-slate-500 justify-center">
        <span className="flex items-center gap-1">
          <div className="w-4 h-4 bg-teal-500 rounded"></div> Pass
        </span>
        <span className="flex items-center gap-1">
          <div className="w-4 h-4 bg-red-500 rounded"></div> Fail
        </span>
        <span className="flex items-center gap-1">
          <div className="w-4 h-4 bg-teal-100 rounded border border-teal-200"></div> Low confidence
        </span>
      </div>

      {/* Analysis Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b-2 border-slate-200">
              <th className="text-left py-3 px-2 font-semibold text-slate-700">Investor</th>
              <th className="text-left py-3 px-2 font-semibold text-slate-700">Firm</th>
              <th className="text-center py-3 px-2 font-semibold text-slate-700">Score</th>
              <th className="text-center py-3 px-1 font-semibold text-slate-700" title="Thesis Alignment">
                <span className="hidden md:inline">Thesis</span>
                <span className="md:hidden">T</span>
              </th>
              <th className="text-center py-3 px-1 font-semibold text-slate-700" title="Stage Match">
                <span className="hidden md:inline">Stage</span>
                <span className="md:hidden">S</span>
              </th>
              <th className="text-center py-3 px-1 font-semibold text-slate-700" title="Geography Match">
                <span className="hidden md:inline">Geo</span>
                <span className="md:hidden">G</span>
              </th>
              <th className="text-center py-3 px-2 font-semibold text-slate-700 hidden lg:table-cell">Portfolio</th>
              <th className="text-center py-3 px-2 font-semibold text-slate-700 hidden lg:table-cell">Deploy</th>
              <th className="text-center py-3 px-2 font-semibold text-slate-700 hidden lg:table-cell">Lead</th>
              <th className="text-left py-3 px-2 font-semibold text-slate-700 hidden xl:table-cell">Notes</th>
              <th className="text-left py-3 px-2 font-semibold text-slate-700">Hook</th>
            </tr>
          </thead>
          <tbody>
            {freeResults.map((inv, idx) => (
              <>
                <tr 
                  key={idx} 
                  className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${
                    inv.data_source === "inferred" ? "bg-amber-50/30" : ""
                  }`}
                  onClick={() => setExpandedRow(expandedRow === idx ? null : idx)}
                >
                  {/* Investor Name */}
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600">
                        {inv.name?.charAt(0) || "?"}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900 truncate max-w-[120px]">{inv.name || "—"}</div>
                        {inv.data_source === "inferred" && (
                          <span className="text-[10px] text-amber-600 bg-amber-100 px-1 rounded">Inferred</span>
                        )}
                      </div>
                    </div>
                  </td>
                  
                  {/* Firm */}
                  <td className="py-3 px-2">
                    <div className="truncate max-w-[120px] text-slate-700" title={inv.firm}>{inv.firm || "—"}</div>
                  </td>
                  
                  {/* Match Score */}
                  <td className="py-3 px-2 text-center">
                    <Badge 
                      className={`${
                        inv.match_score >= 70 ? 'bg-teal-600' : 
                        inv.match_score >= 50 ? 'bg-amber-500' : 'bg-slate-400'
                      } text-white`}
                    >
                      {inv.match_score}%
                    </Badge>
                  </td>
                  
                  {/* Hard Gates */}
                  <td className="py-3 px-1 text-center">
                    <div className="flex justify-center" title={inv.gates?.thesis?.reason}>
                      <GateIndicator gate={inv.gates?.thesis} />
                    </div>
                  </td>
                  <td className="py-3 px-1 text-center">
                    <div className="flex justify-center" title={inv.gates?.stage?.reason}>
                      <GateIndicator gate={inv.gates?.stage} />
                    </div>
                  </td>
                  <td className="py-3 px-1 text-center">
                    <div className="flex justify-center" title={inv.gates?.geography?.reason}>
                      <GateIndicator gate={inv.gates?.geography} />
                    </div>
                  </td>
                  
                  {/* Soft Scores - Hidden on smaller screens */}
                  <td className="py-3 px-2 text-center hidden lg:table-cell">
                    <ScoreCell score={inv.scores?.portfolio} />
                  </td>
                  <td className="py-3 px-2 text-center hidden lg:table-cell">
                    <ScoreCell score={inv.scores?.deployment} />
                  </td>
                  <td className="py-3 px-2 text-center hidden lg:table-cell">
                    <ScoreCell score={inv.scores?.leading} />
                  </td>
                  
                  {/* Notes */}
                  <td className="py-3 px-2 hidden xl:table-cell">
                    <div className="text-xs text-slate-600 truncate max-w-[150px]">
                      {inv.notes?.length > 0 ? inv.notes.join("; ") : "—"}
                    </div>
                  </td>
                  
                  {/* Hook */}
                  <td className="py-3 px-2">
                    <div className="text-xs text-slate-600 truncate max-w-[200px]" title={inv.hook}>
                      {inv.hook ? `"${inv.hook.substring(0, 60)}${inv.hook.length > 60 ? '...' : ''}"` : "—"}
                    </div>
                  </td>
                </tr>
                
                {/* Expanded Row Details */}
                {expandedRow === idx && (
                  <tr className="bg-slate-50">
                    <td colSpan={11} className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Gates Detail */}
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-2 text-sm">Hard Gates</h4>
                          <div className="space-y-1 text-xs">
                            <div className="flex items-center gap-2">
                              <GateIndicator gate={inv.gates?.thesis} />
                              <span><strong>Thesis:</strong> {inv.gates?.thesis?.reason || "N/A"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <GateIndicator gate={inv.gates?.stage} />
                              <span><strong>Stage:</strong> {inv.gates?.stage?.reason || "N/A"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <GateIndicator gate={inv.gates?.geography} />
                              <span><strong>Geography:</strong> {inv.gates?.geography?.reason || "N/A"}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Scores Detail */}
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-2 text-sm">Soft Scores</h4>
                          <div className="space-y-2 text-xs">
                            <div>
                              <strong>Portfolio ({inv.scores?.portfolio?.value}/100):</strong>
                              <div className="text-slate-600">{inv.scores?.portfolio?.detail || "N/A"}</div>
                            </div>
                            <div>
                              <strong>Deployment ({inv.scores?.deployment?.value}/100):</strong>
                              <div className="text-slate-600">{inv.scores?.deployment?.detail || "N/A"}</div>
                            </div>
                            <div>
                              <strong>Leading ({inv.scores?.leading?.value}/100):</strong>
                              <div className="text-slate-600">{inv.scores?.leading?.detail || "N/A"}</div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Hook & Notes */}
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-2 text-sm">Suggested Hook</h4>
                          <div className="bg-white p-2 rounded border text-xs italic text-slate-700">
                            "{inv.hook || "No hook available"}"
                          </div>
                          {inv.notes?.length > 0 && (
                            <div className="mt-2">
                              <h4 className="font-semibold text-slate-700 mb-1 text-sm">Notes</h4>
                              <ul className="text-xs text-slate-600 list-disc list-inside">
                                {inv.notes.map((note, i) => <li key={i}>{note}</li>)}
                              </ul>
                            </div>
                          )}
                          {inv.conflicts?.length > 0 && (
                            <div className="mt-2">
                              <h4 className="font-semibold text-amber-700 mb-1 text-sm flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" /> Potential Conflicts
                              </h4>
                              <ul className="text-xs text-amber-600 list-disc list-inside">
                                {inv.conflicts.map((c, i) => <li key={i}>{c}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty state */}
      {allResults.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <h3 className="font-semibold text-slate-700 mb-2">No investors to analyze</h3>
            <p className="text-sm text-slate-500">Go back and add investors to analyze.</p>
          </CardContent>
        </Card>
      )}

      {/* Locked results */}
      {lockedCount > 0 && (
        <Card className="border-dashed border-2 bg-slate-50">
          <CardContent className="py-6 text-center">
            <Lock className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            <h3 className="font-semibold text-slate-900 mb-1">
              +{lockedCount} more investors analyzed
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              Sign up free to unlock all results, save to CRM, and access 575+ curated investors
            </p>
            <Button onClick={onSignUp} className="bg-teal-600 hover:bg-teal-700">
              Sign Up Free
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* CTA */}
      <Card className="bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <CardContent className="py-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-lg mb-1">Ready to supercharge your fundraise?</h3>
              <p className="text-teal-100 text-sm">
                Full access to 575+ investors, CRM pipeline, and strategic intelligence
              </p>
            </div>
            <Button 
              onClick={onSignUp}
              className="bg-white text-teal-700 hover:bg-teal-50 whitespace-nowrap"
            >
              Get Started Free
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Main component
export default function InvestorIntel() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [investorData, setInvestorData] = useState(null);
  const [startupProfile, setStartupProfile] = useState({});

  const handleSignUp = () => {
    // Store data in sessionStorage for post-signup
    sessionStorage.setItem('intel_investors', JSON.stringify(investorData));
    sessionStorage.setItem('intel_profile', JSON.stringify(startupProfile));
    navigate('/auth?from=intel');
  };

  return (
    <div className="min-h-screen bg-[#121318]">
      {/* Header */}
      <header className="border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src="/logo-complete.png" alt="Operon" className="h-10" />
          </a>
          <Button variant="outline" onClick={() => navigate('/auth')} className="text-white border-slate-600 hover:bg-slate-800">
            Sign In
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-2xl mx-auto px-4 py-12">
        {/* Hero */}
        {step === 1 && (
          <div className="text-center mb-10">
            <Badge className="bg-teal-600 text-white mb-4">Free Tool</Badge>
            <h1 className="text-4xl font-bold text-white mb-4">
              Investor Intel
            </h1>
            <p className="text-xl text-slate-400">
              Analyze any investor list. Get personalized conversation starters<br />
              and identify the best matches for your startup.
            </p>
          </div>
        )}

        {/* Steps */}
        <Card className="bg-white">
          <CardContent className="p-6 md:p-8">
            {step === 1 && (
              <InvestorInputStep 
                onNext={() => setStep(2)}
                investorData={investorData}
                setInvestorData={setInvestorData}
              />
            )}
            {step === 2 && (
              <StartupProfileStep 
                onNext={() => setStep(3)}
                onBack={() => setStep(1)}
                startupProfile={startupProfile}
                setStartupProfile={setStartupProfile}
              />
            )}
            {step === 3 && (
              <ResultsStep 
                investorData={investorData}
                startupProfile={startupProfile}
                onBack={() => setStep(2)}
                onSignUp={handleSignUp}
              />
            )}
          </CardContent>
        </Card>

        {/* Trust signals */}
        {step === 1 && (
          <div className="mt-8 text-center text-slate-500 text-sm">
            <p>575+ VC firms in our database • Used by UK therapeutic founders</p>
          </div>
        )}
      </main>
    </div>
  );
}
